import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import IntakeShell, { Field, FieldGrid } from '../components/intake/IntakeShell';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Generic intake form. Each /intake/* page passes:
 *   - intake_type (slug)
 *   - title / subtitle / icon for the hero
 *   - fields (an ordered list of field descriptors)
 *   - sideNote (optional small disclaimer block)
 *
 * Automatically picks up `?ref=BROKERCODE` from the URL and writes it into broker_code
 * (or referral_source if no broker_code field is in the schema).
 */
export default function IntakeForm({ intakeType, title, subtitle, icon, fields, sideNote, testid }) {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  // Pull ?ref= broker code on mount + show a small banner
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (!ref) return;
    setForm(prev => ({ ...prev, broker_code: ref, referral_source: prev.referral_source || `ref:${ref}` }));
  }, [searchParams]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    // Validate required
    const missing = fields.filter(f => f.required && !String(form[f.name] || '').trim()).map(f => f.label);
    if (missing.length) { toast.error(`Required: ${missing.join(', ')}`); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/intake/${intakeType}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Submission failed');
      setDone(data);
      toast.success('Request submitted — we\'ll be in touch shortly');
    } catch (err) { toast.error(err.message); }
    setSubmitting(false);
  };

  if (done) {
    return (
      <IntakeShell title={title} subtitle={subtitle} icon={icon} testid={testid}>
        <div className="text-center py-8" data-testid="intake-success">
          <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-black tracking-tight">Request received</h2>
          <p className="text-sm text-gray-600 mt-2 max-w-md mx-auto">{done.next_step}</p>
          <div className="mt-5 inline-block bg-gray-100 rounded-lg px-4 py-2">
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Submission ID</p>
            <p className="font-mono text-sm font-bold mt-1" data-testid="submission-id">{done.submission_id}</p>
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/"><Button variant="outline" data-testid="back-to-home">Back to ACE</Button></Link>
            <Link to="/driven"><Button className="bg-red-600 hover:bg-red-700">Explore Services</Button></Link>
          </div>
        </div>
      </IntakeShell>
    );
  }

  return (
    <IntakeShell title={title} subtitle={subtitle} icon={icon} testid={testid}>
      {form.broker_code && (
        <div className="bg-green-50 border border-green-200 rounded p-3 mb-4 text-xs flex items-center gap-2" data-testid="referral-banner">
          <CheckCircle2 className="h-4 w-4 text-green-700" />
          <span>Referred by partner code <strong className="font-mono">{form.broker_code}</strong>. They'll earn a commission on your booking.</span>
        </div>
      )}
      <form onSubmit={submit} className="space-y-1" data-testid="intake-form">
        {fields.map(f => renderField(f, form, set))}
        {sideNote && (
          <div className="text-[11px] text-gray-500 bg-amber-50 border border-amber-200 rounded p-3 mt-4">
            {sideNote}
          </div>
        )}
        <Button type="submit" disabled={submitting} className="w-full h-12 mt-4 bg-red-600 hover:bg-red-700 text-base font-bold" data-testid="intake-submit">
          {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</> : 'Submit Request'}
        </Button>
      </form>
    </IntakeShell>
  );
}

function renderField(f, form, set) {
  // f: { name, label, type, required, placeholder, hint, options, span }
  const val = form[f.name] ?? (f.type === 'number' ? '' : '');
  const input = (() => {
    if (f.type === 'textarea') return (
      <Textarea value={val} onChange={e => set(f.name, e.target.value)} placeholder={f.placeholder} rows={3} data-testid={`field-${f.name}`} />
    );
    if (f.type === 'select') return (
      <select value={val} onChange={e => set(f.name, e.target.value)} className="w-full h-10 border border-gray-300 rounded-md px-3 text-sm bg-white" data-testid={`field-${f.name}`}>
        <option value="">— Pick one —</option>
        {f.options.map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
      </select>
    );
    return (
      <Input
        type={f.type || 'text'}
        value={val}
        onChange={e => set(f.name, e.target.value)}
        placeholder={f.placeholder}
        data-testid={`field-${f.name}`}
        step={f.type === 'number' ? 'any' : undefined}
      />
    );
  })();

  if (f.span === 'full' || f.type === 'textarea') {
    return <Field key={f.name} label={f.label} required={f.required} hint={f.hint}>{input}</Field>;
  }

  // Group pairs of half-width fields next to each other if marked .pair
  return (
    <Field key={f.name} label={f.label} required={f.required} hint={f.hint}>{input}</Field>
  );
}

/** Helper: wrap two half-fields side-by-side */
export function PairFields({ children }) {
  return <FieldGrid cols={2}>{children}</FieldGrid>;
}
